// --- components/AvalancheTypeEntryList.tsx ---
import React from "react";
import {
  Box,
  TextField,
  MenuItem,
  IconButton,
  Stack,
  Typography,
  Card,
  CardContent,
  CardActions,
  Button,
} from "@mui/material";
import { Checkbox, FormControlLabel } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import { v4 as uuidv4 } from "uuid";
import Rosette from "./Rosette.tsx";
import MultiLevelRose from "./MultiLevelRose.tsx";
import { AvalancheTypeEntry } from "../types/report";

const AVA_TYPES = [
  "Triebschnee",
  "Altschnee",
  "Neuschnee",
  "Gleitschnee",
  "Nassschnee",
];
const GENERAL_SITUATION_OPTIONS = [
  "Keine Angabe",
  "Einfache Lawinensituation",
  "Schwierige Lawinensituation",
  "Komplexe Lawinensituation",
];

const AvalancheTypeEntryList = (props) => {
  const addEntry = () => {
    props.setEntries([
      ...props.entries,
      {
        id: uuidv4(),
        aspects: [],
        type: "",
        altitudeFrom: "",
        altitudeTo: "",
        drawMiddleLayer: false,
      },
    ]);
  };

  const updateEntry = (id: string, updated: Partial<AvalancheTypeEntry>) => {
    props.setEntries((prev) =>
      prev.map((e) => (e.id === id ? { ...e, ...updated } : e))
    );
  };

  const removeEntry = (id: string) => {
    props.setEntries((prev) => prev.filter((e) => e.id !== id));
  };

  const moveEntryUp = (index: number) => {
    if (index === 0) return;
    const newEntries = [...props.entries];
    [newEntries[index - 1], newEntries[index]] = [
      newEntries[index],
      newEntries[index - 1],
    ];
    props.setEntries(newEntries);
  };

  const moveEntryDown = (index: number) => {
    if (index === props.entries.length - 1) return;
    const newEntries = [...props.entries];
    [newEntries[index], newEntries[index + 1]] = [
      newEntries[index + 1],
      newEntries[index],
    ];
    props.setEntries(newEntries);
  };

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: {
          xs: "1fr",
          sm: "repeat(auto-fill, minmax(320px, 1fr))",
        },
        gap: 3,
      }}
    >
      {/* Lawinensituation Auswahl */}
      <TextField
        select
        label="Allgemeine Lawinensituation"
        value={props.avalancheSituation}
        onChange={(e) => props.setAvalancheSituation(e.target.value)}
        fullWidth
      >
        {GENERAL_SITUATION_OPTIONS.map((option) => (
          <MenuItem key={option} value={option}>
            {option}
          </MenuItem>
        ))}
      </TextField>
      {props.entries.map((entry, index) => (
        <Card
          key={entry.id}
          sx={{
            backgroundColor: "white",
            display: "flex",
            flexDirection: "column",
            borderRadius: "12px",
            boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
            transition: "all 0.3s ease",
            "&:hover": {
              boxShadow: "0 8px 24px rgba(102, 126, 234, 0.15)",
              transform: "translateY(-4px)",
            },
          }}
        >
          <CardContent
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 2,
            }}
          >
            <Typography variant="subtitle1">Eintrag {index + 1}</Typography>
            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", sm: "row" },
                width: "100%",
                gap: 2,
              }}
            >
              <Box
                sx={{
                  flex: 1,
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <Rosette
                  selected={entry.aspects}
                  onChange={(aspects) => updateEntry(entry.id, { aspects })}
                />
              </Box>
              <Box
                sx={{
                  flex: 1,
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <MultiLevelRose
                  conditionData={[
                    [
                      entry.altitudeFrom || null,
                      entry.altitudeTo || null,
                      entry.aspects.map((aspect) => ({ expo: aspect })),
                      entry.drawMiddleLayer,
                    ],
                  ]}
                  cssIdentifier={`avalanche-type-${entry.id}`}
                  width="150px"
                  height="150px"
                  noHeight
                />
              </Box>
            </Box>
            <TextField
              select
              label="Schneetyp"
              value={entry.type}
              onChange={(e) => updateEntry(entry.id, { type: e.target.value })}
              fullWidth
            >
              {AVA_TYPES.map((type) => (
                <MenuItem key={type} value={type}>
                  {type}
                </MenuItem>
              ))}
            </TextField>
            <Stack direction="row" spacing={2}>
              <TextField
                label="Höhe von"
                type="number"
                value={entry.altitudeFrom}
                onChange={(e) =>
                  updateEntry(entry.id, {
                    altitudeFrom: e.target.value,
                    drawMiddleLayer:
                      e.target.value && !entry.altitudeTo
                        ? entry.drawMiddleLayer
                        : false,
                  })
                }
                fullWidth
              />
              <TextField
                label="Höhe bis"
                type="number"
                value={entry.altitudeTo}
                onChange={(e) =>
                  updateEntry(entry.id, {
                    altitudeTo: e.target.value,
                    drawMiddleLayer:
                      e.target.value && !entry.altitudeFrom
                        ? entry.drawMiddleLayer
                        : false,
                  })
                }
                fullWidth
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={entry.drawMiddleLayer ?? false}
                    onChange={(e) =>
                      updateEntry(entry.id, {
                        drawMiddleLayer: e.target.checked,
                      })
                    }
                    disabled={
                      !(
                        (!!entry.altitudeFrom && !entry.altitudeTo) ||
                        (!entry.altitudeFrom && !!entry.altitudeTo)
                      )
                    }
                  />
                }
                label="Mittellayer füllen"
              />
            </Stack>
          </CardContent>
          <CardActions sx={{ justifyContent: "space-between" }}>
            <Stack direction="row" spacing={1}>
              <IconButton
                onClick={() => moveEntryUp(index)}
                disabled={index === 0}
                size="small"
                title="Nach oben"
              >
                <ArrowUpwardIcon />
              </IconButton>
              <IconButton
                onClick={() => moveEntryDown(index)}
                disabled={index === props.entries.length - 1}
                size="small"
                title="Nach unten"
              >
                <ArrowDownwardIcon />
              </IconButton>
            </Stack>
            <IconButton onClick={() => removeEntry(entry.id)} title="Löschen">
              <DeleteIcon />
            </IconButton>
          </CardActions>
        </Card>
      ))}

      {/* Add new entry card */}
      <Card
        onClick={addEntry}
        sx={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          border: "2px dashed #667eea",
          backgroundColor: "rgba(102, 126, 234, 0.05)",
          borderRadius: "12px",
          cursor: "pointer",
          minHeight: 220,
          transition: "all 0.3s ease",
          "&:hover": {
            backgroundColor: "rgba(102, 126, 234, 0.1)",
            borderColor: "#764ba2",
            transform: "translateY(-4px)",
            boxShadow: "0 8px 24px rgba(102, 126, 234, 0.2)",
          },
        }}
      >
        <CardContent
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <AddIcon sx={{ fontSize: 48, color: "#667eea", mb: 2 }} />
          <Typography variant="h6" sx={{ color: "#667eea", fontWeight: 600 }}>
            Lawinentyp hinzufügen
          </Typography>
        </CardContent>
        <CardActions sx={{ justifyContent: "center" }}>
          <Button
            size="small"
            sx={{
              color: "#667eea",
              fontWeight: 600,
              "&:hover": { backgroundColor: "rgba(102, 126, 234, 0.1)" },
            }}
          >
            Hinzufügen
          </Button>
        </CardActions>
      </Card>
    </Box>
  );
};

export default AvalancheTypeEntryList;
